import React, { useState, useEffect, useMemo, useRef, useId, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, ChevronDown, Check, Pencil } from 'lucide-react';
import { urlPreviewUtils } from '../utils/urlPreview';
import { templateUtils, useTemplateManager } from '../utils/templateUtils';
import EmailTemplateFormFields from './EmailTemplateFormFields';

// Template Preview Component for individual template display
const TemplatePreview = ({
  template,
  campaignId,
  campaignStatus,
  canEditBody,
  onTemplateUpdated,
  editKey,
  onBodyEditStateChange
}) => {
  // Each TemplatePreview gets its own independent hook instance
  const {
    formData: templateData,
    linkPreviews,
    attachments,
    loadingPreview,
    uploadingFile,
    initializeEditForm,
    handleInputChange,
    updateTemplate
  } = useTemplateManager();
  const [isEditingBody, setIsEditingBody] = useState(false);
  const [savingBody, setSavingBody] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');
  const [sharedUsage, setSharedUsage] = useState(null);

  // Initialize template data - this must be called before any conditional returns to follow React hooks rules
  useEffect(() => {
    if (template) {
      initializeEditForm(template);
    }
  }, [template?.id, template?.body]); // Keep other previews in sync after a shared update

  useEffect(() => () => {
    onBodyEditStateChange?.(editKey, false);
  }, [editKey, onBodyEditStateChange]);

  if (!template) return <p className="text-sm text-gray-500">Template not found</p>;

  const bodyHasText = (templateData.body || '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0;

  const saveTemplateBody = async (confirmSharedUpdate = false) => {
    if (!bodyHasText) {
      setSaveError('Email body is required.');
      return;
    }

    try {
      setSavingBody(true);
      setSaveError('');
      const templateId = template.template_id ?? template.id;
      const response = await updateTemplate(templateId, {
        campaign_id: campaignId,
        confirm_shared_update: confirmSharedUpdate,
        expected_body: template.body
      }, { body: templateData.body });
      const updatedTemplate = response.template || { ...template, body: templateData.body };
      initializeEditForm(updatedTemplate);
      setSharedUsage(null);
      setIsEditingBody(false);
      onBodyEditStateChange?.(editKey, false);
      setSaveMessage('Email body updated.');
      await onTemplateUpdated?.(updatedTemplate);
    } catch (error) {
      const response = error.response?.data;
      if (response?.code === 'TEMPLATE_IN_USE') {
        setSharedUsage(response);
      } else if (response?.code === 'TEMPLATE_CHANGED' && response.template) {
        initializeEditForm(response.template);
        await onTemplateUpdated?.(response.template);
        setIsEditingBody(false);
        onBodyEditStateChange?.(editKey, false);
        setSaveError(response.error);
      } else {
        setSaveError(response?.error || error.message || 'Failed to update the email body.');
      }
    } finally {
      setSavingBody(false);
    }
  };

  const cancelBodyEdit = () => {
    initializeEditForm(template);
    setIsEditingBody(false);
    onBodyEditStateChange?.(editKey, false);
    setSaveError('');
    setSaveMessage('');
  };

  return (
    <>
      {canEditBody && (
        <div className="flex items-center justify-end mb-3">
          {!isEditingBody ? (
            <button
              type="button"
              onClick={() => {
                setIsEditingBody(true);
                onBodyEditStateChange?.(editKey, true);
                setSaveError('');
                setSaveMessage('');
              }}
              disabled={campaignStatus === 'sending'}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              title={campaignStatus === 'sending' ? 'Pause the campaign before editing its email body.' : undefined}
            >
              <Pencil size={14} />
              Edit email body
            </button>
          ) : (
            <span className="text-xs text-amber-700">
              This changes the shared template.
            </span>
          )}
        </div>
      )}

      {canEditBody && campaignStatus === 'sending' && (
        <p className="mb-3 text-xs text-amber-700">Pause the campaign before editing its email body.</p>
      )}

      <EmailTemplateFormFields
        formData={templateData}
        onInputChange={isEditingBody ? handleInputChange : () => { }}
        onFileSelect={() => { }}
        onRemoveAttachment={() => { }}
        linkPreviews={linkPreviews}
        loadingPreview={loadingPreview}
        attachments={attachments}
        uploadingFile={uploadingFile}
        disabled={savingBody}
        compact={true}
        templateReadOnly={true}
        editableBody={isEditingBody}
      />

      {isEditingBody && (
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={cancelBodyEdit}
            disabled={savingBody}
            className="px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveTemplateBody(false)}
            disabled={savingBody || !bodyHasText}
            className="px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {savingBody ? 'Saving...' : 'Save email body'}
          </button>
        </div>
      )}

      {saveError && <p className="mt-2 text-sm text-red-600">{saveError}</p>}
      {saveMessage && <p className="mt-2 text-sm text-green-700">{saveMessage}</p>}

      {sharedUsage && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`shared-template-warning-${template.id}`}
            className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
          >
            <h3 id={`shared-template-warning-${template.id}`} className="text-lg font-semibold text-gray-900">
              Update a shared template?
            </h3>
            <p className="mt-2 text-sm text-gray-700">
              This template is used by {sharedUsage.campaign_count} other campaign{sharedUsage.campaign_count === 1 ? '' : 's'}
              {' '}in {sharedUsage.usage_count} campaign step{sharedUsage.usage_count === 1 ? '' : 's'}.
              Future unsent emails in these campaigns will use the new body.
            </p>
            <div className="mt-4 max-h-56 overflow-y-auto rounded-lg border border-gray-200">
              {sharedUsage.campaigns.map(campaign => (
                <div key={campaign.campaign_id} className="flex items-center justify-between gap-4 border-b border-gray-100 px-3 py-2 last:border-b-0">
                  <span className="text-sm text-gray-800">{campaign.campaign_name}</span>
                  <span className="whitespace-nowrap text-xs capitalize text-gray-500">
                    {campaign.status} · {campaign.usage_count} step{Number(campaign.usage_count) === 1 ? '' : 's'}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSharedUsage(null)}
                disabled={savingBody}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => saveTemplateBody(true)}
                disabled={savingBody}
                className="px-4 py-2 text-sm text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {savingBody ? 'Updating...' : 'Update shared template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SearchableTemplateSelect = ({ templates, value, onChange, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const optionsId = useId();

  const sortedTemplates = useMemo(() => [...templates].sort((first, second) => {
    if (first.source === 'campaign_generated' && second.source !== 'campaign_generated') return 1;
    if (first.source !== 'campaign_generated' && second.source === 'campaign_generated') return -1;
    return new Date(second.created_at) - new Date(first.created_at);
  }), [templates]);

  const selectedTemplate = sortedTemplates.find(template => String(template.id) === String(value));
  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sortedTemplates;

    return sortedTemplates.filter(template => [template.name, template.type, template.subject]
      .some(field => String(field || '').toLowerCase().includes(term)));
  }, [searchTerm, sortedTemplates]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const selectTemplate = (templateId) => {
    onChange(templateId);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setSearchTerm('');
    setHighlightedIndex(0);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (!isOpen && ['Enter', 'ArrowDown', ' '].includes(event.key)) {
      event.preventDefault();
      openDropdown();
      return;
    }

    if (!isOpen) return;

    const optionCount = filteredTemplates.length + 1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(current => (current + 1) % optionCount);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(current => (current - 1 + optionCount) % optionCount);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      selectTemplate(highlightedIndex === 0 ? '' : filteredTemplates[highlightedIndex - 1].id);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const selectedLabel = selectedTemplate
    ? `${selectedTemplate.name} (${selectedTemplate.type})`
    : '';

  return (
    <div ref={containerRef} className="relative">
      <Search
        size={17}
        className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
      />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={optionsId}
        value={isOpen ? searchTerm : selectedLabel}
        onFocus={openDropdown}
        onChange={(event) => {
          setSearchTerm(event.target.value);
          setHighlightedIndex(0);
          if (!isOpen) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Select a template or create from scratch"
        className={`w-full py-3 pl-10 pr-10 border rounded-lg ${disabled
          ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
          : 'border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
          }`}
        disabled={disabled}
        autoComplete="off"
      />
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            setSearchTerm('');
          } else {
            openDropdown();
            inputRef.current?.focus();
          }
        }}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 disabled:text-gray-300"
        disabled={disabled}
        tabIndex={-1}
        aria-label="Toggle template options"
      >
        <ChevronDown size={18} className={isOpen ? 'rotate-180' : ''} />
      </button>

      {isOpen && (
        <div
          id={optionsId}
          role="listbox"
          className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          <button
            type="button"
            role="option"
            aria-selected={!value}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => selectTemplate('')}
            className={`w-full px-3 py-3 text-left flex items-center justify-between hover:bg-blue-50 ${highlightedIndex === 0 ? 'bg-blue-50' : ''}`}
          >
            <span>
              <span className="block text-sm font-medium text-gray-800">Create from scratch</span>
              <span className="block text-xs text-gray-500">Write a new subject and email body</span>
            </span>
            {!value && <Check size={16} className="text-blue-600" />}
          </button>

          {filteredTemplates.length > 0 ? filteredTemplates.map((template, index) => {
            const isSelected = String(template.id) === String(value);
            const isHighlighted = highlightedIndex === index + 1;
            return (
              <button
                key={template.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectTemplate(template.id)}
                className={`w-full px-3 py-3 text-left border-t border-gray-100 flex items-center justify-between gap-3 hover:bg-blue-50 ${isHighlighted ? 'bg-blue-50' : ''}`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-800 truncate">{template.name}</span>
                  <span className="block text-xs text-gray-500 truncate">
                    {[template.type, template.subject].filter(Boolean).join(' · ') || 'Email template'}
                  </span>
                </span>
                {isSelected && <Check size={16} className="flex-shrink-0 text-blue-600" />}
              </button>
            );
          }) : (
            <p className="px-3 py-4 text-sm text-center text-gray-500 border-t border-gray-100">
              No templates match your search.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const encodeLegacyFrequency = (amount, unit) => {
  const normalizedAmount = Math.max(1, Number.parseInt(amount, 10) || 1);
  if (unit === 'weeks') return normalizedAmount;

  const hoursByUnit = {
    hours: 1,
    days: 24,
    months: 30 * 24
  };
  return 1000 + normalizedAmount * (hoursByUnit[unit] || 1);
};

const getFrequencyFields = (template) => {
  if (template.frequency_amount && ['hours', 'days', 'weeks', 'months'].includes(template.frequency_unit)) {
    return {
      amount: Number(template.frequency_amount),
      unit: template.frequency_unit
    };
  }

  const legacyFrequency = Number(template.frequency_weeks) || 2;
  return legacyFrequency < 1000
    ? { amount: legacyFrequency, unit: 'weeks' }
    : { amount: legacyFrequency - 1000, unit: 'hours' };
};

/**
 * Enhanced Campaign Modal Component
 * Self-contained component for creating and editing email campaigns
 * Manages all campaign modal state and operations internally
 */
const CampaignModal = ({
  // Modal control props
  isOpen,
  onClose,
  templates = [],
  // Campaign props
  editingCampaign = null, // When provided, opens in edit mode
  selectedContacts = null, // When provided, creates campaign with selected contacts
  managedRecipients = null,

  // External handlers
  gmailStatus = null,
  loadCampaigns = () => { },
  onTemplateBodyUpdated = () => { },
  onSuccess = () => { },
  setShowContactPanel = null, // Function to show contact management panel

  // Optional overrides
  initialFormData = {
    name: '',
    target_type: 'cold',
    email_account: '', // Email account to use for sending
    selected_tags: [], // Tags to filter contacts by
    selected_companies: [], // Company IDs to filter contacts by
    click_count: '0', // Number of clicks to filter by
    templates: [{ template_id: '', sequence_order: 0, frequency_weeks: 0, frequency_amount: 2, frequency_unit: 'weeks', use_date: false, scheduled_at: '' }], // Array of templates
    scheduled_at: '',
    selected_contacts: null,
    days: JSON.stringify(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']),
    start_time: '06:00',
    end_time: '18:00',
    time_zone: 'America/New_York',
    daily_limit: 100,
  },
  title = null // Auto-generated if not provided
}) => {
  // Use the shared template manager hook for template data (subject, body, link_previews, attachments)
  const {
    loading: hookLoading,
    formData: templateData,
    resetForm: resetTemplateForm,
    initializeEditForm,
    handleInputChange,
    handleFileSelect,
    handleRemoveAttachment,
    linkPreviews,
    loadingPreview,
    attachments,
    uploadingFile,
    templateUtils: utils,
    createTemplate,
  } = useTemplateManager();

  // Campaign-specific state (separate from template data)
  const [campaignData, setCampaignData] = useState(initialFormData);

  // Local templates list — augmented with any auto-created templates not in the prop list
  const [localTemplates, setLocalTemplates] = useState(templates);

  // Gmail accounts state
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [loadingGmailAccounts, setLoadingGmailAccounts] = useState(false);

  // Tags state
  const [availableTags, setAvailableTags] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);

  // Companies state
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companySearch, setCompanySearch] = useState('');

  // Matching audience count
  const [audienceCount, setAudienceCount] = useState(null);
  const [loadingAudienceCount, setLoadingAudienceCount] = useState(false);
  const [audienceCountError, setAudienceCountError] = useState(false);
  const [followUpPreview, setFollowUpPreview] = useState(null);
  const [loadingFollowUpPreview, setLoadingFollowUpPreview] = useState(false);
  const [includeOutOfOffice, setIncludeOutOfOffice] = useState(false);
  const [excludeReplied, setExcludeReplied] = useState(true);

  // Internal state management for modal-specific features
  const [loading, setLoading] = useState(false);
  const [activeBodyEdits, setActiveBodyEdits] = useState({});

  // Track initialization to prevent infinite loops
  const [isInitialized, setIsInitialized] = useState(false);

  // Preview contacts for a selected tag
  const [tagPreview, setTagPreview] = useState({
    tag: null,
    contacts: [],
    loading: false,
    error: null
  });
  const [tagSearch, setTagSearch] = useState('');

  // Store original templates for comparison to detect changes
  const [originalTemplates, setOriginalTemplates] = useState([]);

  // Store original scheduled_at for comparison to detect changes
  const [originalScheduledAt, setOriginalScheduledAt] = useState('');
  const [originalTimeZone, setOriginalTimeZone] = useState('');
  const [originalStartTime, setOriginalStartTime] = useState('');

  const handleBodyEditStateChange = useCallback((editKey, isEditing) => {
    setActiveBodyEdits(current => {
      if (isEditing) {
        return current[editKey] ? current : { ...current, [editKey]: true };
      }

      if (!current[editKey]) return current;
      const next = { ...current };
      delete next[editKey];
      return next;
    });
  }, []);

  const hasActiveBodyEdits = Object.keys(activeBodyEdits).length > 0;

  // Determine if we're in edit mode
  const isEditMode = !!editingCampaign;
  const campaignWasStarted = isEditMode && (
    Number(editingCampaign?.sent_count) > 0
    || Number(editingCampaign?.current_round) > 0
    || ['sending', 'completed'].includes(editingCampaign?.status)
    || (Array.isArray(campaignData.selected_contacts)
      && campaignData.selected_contacts.some(recipient => Boolean(recipient.last_sent_at)))
  );

  // Auto-generate title if not provided
  const modalTitle = title || (isEditMode
    ? `Edit Campaign: ${editingCampaign?.name || 'Campaign'}`
    : selectedContacts
      ? `Create Campaign for ${selectedContacts.length} Selected Contacts`
      : 'Create New Campaign');

  const filteredTags = useMemo(() => {
    const term = tagSearch.trim().toLowerCase();
    if (!term) {
      return availableTags;
    }
    return availableTags.filter((tag) => tag.toLowerCase().includes(term));
  }, [availableTags, tagSearch]);

  const filteredCompanies = useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    if (!term) {
      return availableCompanies;
    }
    return availableCompanies.filter((company) => String(company.name || '').toLowerCase().includes(term));
  }, [availableCompanies, companySearch]);

  // Fetch Gmail accounts when modal opens
  const fetchGmailAccounts = async () => {
    if (loadingGmailAccounts) return;

    try {
      setLoadingGmailAccounts(true);
      const response = await axios.get('/api/gmail/accounts');
      if (response.data && response.data.accounts) {
        setGmailAccounts(response.data.accounts);

        // Auto-select primary account if no email account is set and not editing
        if (!campaignData.email_account && !editingCampaign) {
          const primaryAccount = response.data.accounts.find(account => account.is_primary);
          if (primaryAccount) {
            setCampaignData(prev => ({
              ...prev,
              email_account: primaryAccount.email
            }));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch Gmail accounts:', error);
      setGmailAccounts([]);
    } finally {
      setLoadingGmailAccounts(false);
    }
  };

  // Fetch available tags when modal opens
  const fetchAvailableTags = async () => {
    if (loadingTags) return;

    try {
      setLoadingTags(true);
      const response = await axios.get('/api/contact-management/tags');
      if (response.data && response.data.success && Array.isArray(response.data.tags)) {
        let tags = response.data.tags;
        let index = tags.indexOf('block');
        if (index !== -1) {
          tags.splice(index, 1);
        }
        setAvailableTags(tags);
      }
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      setAvailableTags([]);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleTagPreview = async (tag) => {
    if (!tag) return;

    // Toggle preview off if the same tag is clicked again and not loading
    if (tagPreview.tag === tag && !tagPreview.loading) {
      setTagPreview({ tag: null, contacts: [], loading: false, error: null });
      return;
    }

    setTagPreview({
      tag,
      contacts: [],
      loading: true,
      error: null
    });

    try {
      const params = new URLSearchParams({
        tag,
        limit: '15',
        page: '1',
        excludePaused: 'true'
      });
      const response = await axios.get(`/api/contact-management/contacts?${params.toString()}`);
      if (response.data?.success) {
        setTagPreview({
          tag,
          contacts: response.data.contacts || [],
          loading: false,
          error: null
        });
      } else {
        setTagPreview({
          tag,
          contacts: [],
          loading: false,
          error: response.data?.error || 'Failed to load contacts for this tag.'
        });
      }
    } catch (error) {
      console.error(`Failed to load contacts for tag ${tag}:`, error);
      setTagPreview({
        tag,
        contacts: [],
        loading: false,
        error: 'Failed to load contacts for this tag.'
      });
    }
  };

  // Search the company directory while the create form is open.
  useEffect(() => {
    if (!isOpen || isEditMode || selectedContacts) return undefined;

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoadingCompanies(true);
        const response = await axios.get('/api/contact-management/companies', {
          params: { search: companySearch.trim(), limit: 100 },
          signal: controller.signal
        });

        if (response.data?.success && Array.isArray(response.data.companies)) {
          setAvailableCompanies((currentCompanies) => {
            const companiesById = new Map(currentCompanies.map(company => [Number(company.id), company]));
            response.data.companies.forEach(company => companiesById.set(Number(company.id), company));
            return Array.from(companiesById.values())
              .sort((first, second) => String(first.name || '').localeCompare(String(second.name || '')));
          });
        }
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('Failed to fetch companies:', error);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingCompanies(false);
        }
      }
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [isOpen, isEditMode, selectedContacts, companySearch]);

  // Keep the recipient estimate aligned with the filters used at creation.
  useEffect(() => {
    if (!isOpen || isEditMode || selectedContacts) {
      setAudienceCount(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoadingAudienceCount(true);
        setAudienceCountError(false);
        const response = await axios.post('/api/email-campaigns/audience-count', {
          target_type: campaignData.target_type || 'cold',
          selected_tags: campaignData.selected_tags || [],
          selected_company_ids: campaignData.selected_companies || [],
          click_count: campaignData.click_count || '0'
        }, {
          signal: controller.signal
        });

        setAudienceCount(response.data?.success ? response.data.count : null);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('Failed to count campaign audience:', error);
          setAudienceCount(null);
          setAudienceCountError(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingAudienceCount(false);
        }
      }
    }, 350);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    isOpen,
    isEditMode,
    selectedContacts,
    campaignData.target_type,
    campaignData.selected_tags,
    campaignData.selected_companies,
    campaignData.click_count
  ]);

  useEffect(() => {
    const newFollowUp = campaignData.templates?.[originalTemplates.length];
    if (!isOpen || !campaignWasStarted || !isInitialized || !newFollowUp) {
      setFollowUpPreview(null);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setLoadingFollowUpPreview(true);
        const scheduledAt = newFollowUp.use_date && newFollowUp.scheduled_at
          ? templateUtils.dateToUTC(newFollowUp.scheduled_at, campaignData.time_zone, campaignData.start_time)
          : null;
        const response = await axios.post(
          `/api/email-campaigns/${editingCampaign.id}/follow-up-preview`,
          {
            use_date: Boolean(newFollowUp.use_date),
            scheduled_at: scheduledAt,
            frequency_weeks: newFollowUp.frequency_weeks,
            frequency_amount: newFollowUp.frequency_amount,
            frequency_unit: newFollowUp.frequency_unit,
            clicks_filter: newFollowUp.clicks_filter || 0,
            sequence_order: newFollowUp.sequence_order,
            include_out_of_office: includeOutOfOffice,
            include_replied: !excludeReplied
          },
          { signal: controller.signal }
        );
        setFollowUpPreview(response.data?.summary || null);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') {
          console.error('Failed to preview follow-up recipients:', error);
          setFollowUpPreview(null);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingFollowUpPreview(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [
    isOpen,
    campaignWasStarted,
    isInitialized,
    editingCampaign?.id,
    originalTemplates.length,
    campaignData.templates,
    campaignData.time_zone,
    campaignData.start_time,
    includeOutOfOffice,
    excludeReplied
  ]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false);
      return;
    }
    if (isInitialized) return; // Prevent re-initialization

    fetchGmailAccounts();
    fetchAvailableTags();
    initializeForm().catch(error => {
      console.error('Failed to load the latest campaign templates:', error);
      alert('The latest campaign templates could not load. Close the campaign and try again.');
      setIsInitialized(true);
    });
  }, [isOpen, editingCampaign?.id]); // Only depend on isOpen and campaign ID




  const initializeForm = async () => {

    if (editingCampaign) {
      // Edit mode - load campaign data
      let formattedScheduledAt = '';
      if (editingCampaign.scheduled_at) {
        formattedScheduledAt = utils.utcToTimeZone(editingCampaign.scheduled_at, editingCampaign.time_zone);
      }

      // Load the current recipients for every campaign type.
      let campaignSelectedContacts = null;
      try {
        const response = await axios.get(`/api/email-campaigns/${editingCampaign.id}/recipients`);
        if (response.data.success) {
          campaignSelectedContacts = response.data.recipients || [];
        }
      } catch (error) {
        console.error('Failed to fetch campaign recipients:', error);
      }

      // Load templates from campaign.templates array
      let campaignTemplates = [{ template_id: '', sequence_order: 0, frequency_weeks: 0, frequency_amount: 2, frequency_unit: 'weeks', use_date: false, scheduled_at: '' }];

      if (editingCampaign.templates && editingCampaign.templates.length > 0) {
        // Use templates array structure
        campaignTemplates = editingCampaign.templates.map(t => {
          const frequency = getFrequencyFields(t);
          return {
            template_id: t.template_id,
            sequence_order: t.sequence_order,
            frequency_weeks: t.frequency_weeks || 0,
            frequency_amount: frequency.amount,
            frequency_unit: frequency.unit,
            use_date: t.use_date || false,
            scheduled_at: t.scheduled_at ? utils.utcToTimeZone(t.scheduled_at, editingCampaign.time_zone) : '',
            clicks_filter: t.clicks_filter || 0
          };
        });

        // Always load assigned templates from the server.
        const templateIds = [...new Set(editingCampaign.templates
          .map(t => t.template_id)
          .filter(Boolean))];
        const fetched = await Promise.all(
          templateIds.map(id =>
            axios.get(`/api/email-templates/${id}`, { params: { fresh: Date.now() } })
              .then(response => {
                if (!response.data.success || !response.data.template) {
                  throw new Error(`Template ${id} could not load`);
                }
                return response.data.template;
              })
          )
        );
        const fetchedById = new Map(fetched.filter(Boolean).map(template => [String(template.id), template]));
        const augmentedTemplates = templates.map(template => (
          fetchedById.get(String(template.id)) || template
        ));
        fetched.forEach(template => {
          if (template && !augmentedTemplates.some(item => String(item.id) === String(template.id))) {
            augmentedTemplates.push(template);
          }
        });
        setLocalTemplates(augmentedTemplates);

        // Load initial template (sequence_order = 0) for display
        const initialTemplate = editingCampaign.templates.find(t => t.sequence_order === 0);
        if (initialTemplate) {
          const template = augmentedTemplates.find(t => t.id === initialTemplate.template_id || t.id === parseInt(initialTemplate.template_id));
          if (template) {
            initializeEditForm(template);
          }
        }
      }

      // Parse target_type to extract the audience filters.
      let baseTargetType = editingCampaign.target_type;
      let extractedTags = [];
      let extractedClickCount = '0';
      let extractedCompanyIds = [];

      if (editingCampaign.target_type && editingCampaign.target_type.includes('|')) {
        const parts = editingCampaign.target_type.split('|');
        baseTargetType = parts[0];

        // Handle tags (second part)
        if (parts[1] && parts[1].trim() !== '') {
          extractedTags = parts[1].split(',').filter(tag => tag.trim() !== '');
        }

        // Handle click_count (third part)
        if (parts[2]) {
          extractedClickCount = parts[2];
        }

        // Handle company IDs (fourth part)
        if (parts[3] && parts[3].trim() !== '') {
          extractedCompanyIds = parts[3]
            .split(',')
            .map(Number)
            .filter(companyId => Number.isInteger(companyId) && companyId > 0);
        }
      }

      if (extractedCompanyIds.length > 0) {
        const selectedCompanies = await Promise.all(
          extractedCompanyIds.map(companyId =>
            axios.get(`/api/contact-management/companies/${companyId}`)
              .then(response => response.data?.success ? response.data.company : null)
              .catch(() => null)
          )
        );
        setAvailableCompanies(selectedCompanies.filter(Boolean));
      } else {
        setAvailableCompanies([]);
      }

      // Set campaign-specific fields separately
      setCampaignData({
        name: editingCampaign.name,
        target_type: baseTargetType,
        email_account: editingCampaign.email_account || '',
        selected_tags: extractedTags,
        selected_companies: extractedCompanyIds,
        click_count: extractedClickCount,
        templates: campaignTemplates,
        scheduled_at: formattedScheduledAt,
        selected_contacts: campaignSelectedContacts,
        days: editingCampaign.days || JSON.stringify(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']),
        start_time: editingCampaign.start_time || '06:00',
        end_time: editingCampaign.end_time || '18:00',
        time_zone: editingCampaign.time_zone || 'America/New_York',
        daily_limit: editingCampaign.daily_limit || 100
      });

      // Store original templates for change detection
      setOriginalTemplates(campaignTemplates);

      // Store original scheduled_at for change detection
      setOriginalScheduledAt(formattedScheduledAt);
      setOriginalTimeZone(editingCampaign.time_zone);
      setOriginalStartTime(editingCampaign.start_time);
    } else {
      // Create mode - reset both template and campaign forms
      resetTemplateForm();
      setCampaignData(initialFormData);
      // Reset original templates for create mode
      setOriginalTemplates([]);
      // Reset original scheduled_at for create mode
      setOriginalScheduledAt('');
      setOriginalTimeZone('America/New_York');
      setOriginalStartTime('06:00')
    }

    setIsInitialized(true);
  };




  // Form handlers
  const handleCampaignInputChange = (e) => {
    const { name, value } = e.target;
    setCampaignData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle tag selection (multi-select)
  const handleTagChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setCampaignData(prev => ({
      ...prev,
      selected_tags: selectedOptions
    }));
  };

  // Helper function to get template data for display
  const getTemplateForDisplay = (templateId) => {
    if (!templateId) return null;
    return localTemplates.find(t => t.id === parseInt(templateId) || t.id === templateId);
  };

  const updateLocalTemplateBody = async updatedTemplate => {
    setLocalTemplates(currentTemplates => currentTemplates.map(template => (
      String(template.id) === String(updatedTemplate.id)
        ? { ...template, ...updatedTemplate }
        : template
    )));
    await onTemplateBodyUpdated(updatedTemplate);
  };

  // Helper function to check if templates have changed
  const haveTemplatesChanged = (currentTemplates, originalTemplates) => {
    // If arrays have different lengths, they've changed
    if (currentTemplates.length !== originalTemplates.length) {
      return true;
    }

    // Compare each template for changes in key fields
    for (let i = 0; i < currentTemplates.length; i++) {
      const current = currentTemplates[i];
      const original = originalTemplates[i];

      if (!current || !original) {
        return true;
      }

      // Compare relevant fields for changes
      if (current.template_id !== original.template_id ||
        current.sequence_order !== original.sequence_order ||
        current.frequency_weeks !== original.frequency_weeks ||
        current.frequency_amount !== original.frequency_amount ||
        current.frequency_unit !== original.frequency_unit ||
        current.use_date !== original.use_date ||
        current.scheduled_at !== original.scheduled_at
        || current.clicks_filter !== original.clicks_filter) {
        return true;
      }
    }

    return false;
  };

  const handleTemplateChange = (templateIndex, field, value) => {

    setCampaignData(prev => {
      const newTemplates = [...prev.templates];
      newTemplates[templateIndex] = {
        ...newTemplates[templateIndex],
        [field]: value
      };
      return {
        ...prev,
        templates: newTemplates
      };
    });

    // If changing template_id for the first template, load it into the shared hook for editing
    if (field === 'template_id' && templateIndex === 0) {
      const selectedTemplate = localTemplates.find(t => t.id === parseInt(value) || t.id === value);
      if (selectedTemplate) {
        initializeEditForm(selectedTemplate);
      } else {
        resetTemplateForm();
      }
    }
  };

  const addTemplate = () => {
    setCampaignData(prev => {
      const followupIndex = prev.templates.length;
      return {
        ...prev,
        templates: [
          ...prev.templates,
          {
            template_id: '',
            sequence_order: followupIndex,
            frequency_weeks: 2,
            frequency_amount: 2,
            frequency_unit: 'weeks',
            use_date: false,
            scheduled_at: '',
            name: `${prev.name} Follow-up ${followupIndex}`,
            subject: '',
            body: '',
            linkPreviewToggle: false
          }
        ]
      };
    });
  };

  const removeTemplate = (index) => {
    if (index === 0) return; // Cannot remove the initial template

    setCampaignData(prev => ({
      ...prev,
      templates: prev.templates.filter((_, i) => i !== index)
        .map((template, i) => ({ ...template, sequence_order: i })) // Reorder sequence
    }));
  };

  const handleFrequencyChange = (templateIndex, field, value) => {
    setCampaignData(previous => {
      const templates = [...previous.templates];
      const currentTemplate = templates[templateIndex];
      const amount = field === 'frequency_amount'
        ? Math.max(1, Number.parseInt(value, 10) || 1)
        : currentTemplate.frequency_amount || 2;
      const unit = field === 'frequency_unit'
        ? value
        : currentTemplate.frequency_unit || 'weeks';

      templates[templateIndex] = {
        ...currentTemplate,
        frequency_amount: amount,
        frequency_unit: unit,
        frequency_weeks: encodeLegacyFrequency(amount, unit)
      };

      return { ...previous, templates };
    });
  };

  // Campaign operations
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (hasActiveBodyEdits) {
      alert('Save or cancel the email body edit before updating the campaign.');
      return;
    }

    // Check if Gmail account is connected
    if (gmailStatus && !gmailStatus?.hasAccounts) {
      alert('Please connect a Gmail account before creating a campaign. Go to Settings to connect your Gmail account.');
      return;
    }

    // Check if email account is selected
    if (!campaignData.email_account) {
      alert('Please select an email account to send from.');
      return;
    }

    const followUpWithoutDateIndex = campaignData.templates?.findIndex((template, index) => (
      index > 0 && template.use_date && !template.scheduled_at
    ));
    if (followUpWithoutDateIndex > 0) {
      alert(`Select a date for Follow-up ${followUpWithoutDateIndex}.`);
      return;
    }

    const newFollowUpCount = isEditMode
      ? Math.max(0, (campaignData.templates?.length || 0) - originalTemplates.length)
      : 0;
    if (newFollowUpCount > 0 && campaignWasStarted) {
      if (loadingFollowUpPreview || !followUpPreview) {
        alert('Please wait until the follow-up recipient preview is ready.');
        return;
      }

      const confirmed = window.confirm(
        `Add ${newFollowUpCount === 1 ? 'this follow-up' : `${newFollowUpCount} follow-ups`} to the campaign?\n\n` +
        `${followUpPreview.eligible_immediately} contacts are eligible during the next delivery window.\n` +
        `${followUpPreview.scheduled_later} contacts are scheduled later.\n` +
        `${followUpPreview.excluded} contacts are excluded.`
      );
      if (!confirmed) return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        await handleUpdateCampaign();
      } else {
        await handleCreateCampaign();
      }
    } catch (error) {
      console.error('Failed to submit campaign:', error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} campaign: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    // Process all templates, auto-creating any that have no template_id but have content
    let processedTemplates = [...campaignData.templates];
    let autoCreatedTemplateName = null;

    for (let i = 0; i < processedTemplates.length; i++) {
      if (!processedTemplates[i].template_id) {
        if (i === 0) {
          // Initial email uses shared templateData (subject/body fields)
          if (!templateData.subject?.trim() || !templateData.body?.trim()) {
            alert('Please select a template, or provide a subject and body to create one automatically.');
            return;
          }
          const autoName = templateData.name?.trim() || campaignData.name;
          const result = await createTemplate({ name: autoName, source: 'campaign_generated' });
          autoCreatedTemplateName = autoName;
          processedTemplates[i] = { ...processedTemplates[i], template_id: result.templateId };
        } else {
          // Follow-ups use each entry's own name/subject/body fields
          if (!processedTemplates[i].subject?.trim() || !processedTemplates[i].body?.trim()) {
            alert(`Please select a template for Follow-up ${i}, or provide a subject and body to create one automatically.`);
            return;
          }
          const entry = processedTemplates[i];
          const autoName = entry.name?.trim() || `${campaignData.name} Follow-up ${i}`;
          const result = await createTemplate({ name: autoName, subject: entry.subject, body: entry.body, source: 'campaign_generated' });
          autoCreatedTemplateName = autoCreatedTemplateName || autoName;
          processedTemplates[i] = { ...entry, template_id: result.templateId };
        }
      }
    }

    let validTemplates = processedTemplates.filter(t => t.template_id);

    if (validTemplates.length === 0) {
      alert('Please select a template, or provide a subject and body to create one automatically.');
      return;
    }

    // Construct target_type with tags and click_count appended
    let targetType = selectedContacts ? 'selected_contacts' : campaignData.target_type;
    if (!selectedContacts) {
      const tagsString = campaignData.selected_tags && campaignData.selected_tags.length > 0
        ? campaignData.selected_tags.join(',')
        : '';
      const clickCount = campaignData.click_count || '0';
      const companyIdsString = campaignData.selected_companies && campaignData.selected_companies.length > 0
        ? campaignData.selected_companies.join(',')
        : '';

      if (companyIdsString) {
        targetType = `${campaignData.target_type}|${tagsString}|${clickCount}|${companyIdsString}`;
      } else if (tagsString && clickCount !== '0') {
        targetType = `${campaignData.target_type}|${tagsString}|${clickCount}`;
      } else if (tagsString) {
        targetType = `${campaignData.target_type}|${tagsString}`;
      } else if (clickCount !== '0') {
        targetType = `${campaignData.target_type}||${clickCount}`;
      }
    }

    const submitData = {
      // Campaign fields
      name: campaignData.name,
      target_type: targetType,
      email_account: campaignData.email_account,
      // Use templates array structure
      templates: validTemplates.length > 0 ? validTemplates : undefined,
      scheduled_at: campaignData.scheduled_at || null,
      // Scheduling fields
      days: campaignData.days,
      start_time: campaignData.start_time,
      end_time: campaignData.end_time,
      time_zone: campaignData.time_zone,
      daily_limit: campaignData.daily_limit,
    };

    // Add selected contacts if provided
    if (selectedContacts) {
      submitData.selected_contacts = selectedContacts.map(contact => ({
        id: contact.id,
        email: contact.email,
        first_name: contact.first_name,
        last_name: contact.last_name,
        job_title: contact.job_title,
        company: contact.company,
        phone: contact.phone,
        location: contact.location,
        country: contact.country
      }));
    }

    if (submitData.scheduled_at) {
      submitData.scheduled_at = utils.dateToUTC(submitData.scheduled_at, submitData.time_zone, submitData.start_time);
    }
    if (submitData.templates) {
      submitData.templates = submitData.templates.map(({ name: _n, subject: _s, body: _b, linkPreviewToggle: _l, ...t }) => ({
        ...t,
        scheduled_at: t.scheduled_at ? utils.dateToUTC(t.scheduled_at, submitData.time_zone, submitData.start_time) : t.scheduled_at
      }));
    }

    const response = await axios.post('/api/email-campaigns', submitData);

    if (response.data.success) {
      alert(autoCreatedTemplateName
        ? `Campaign created successfully!\nEmail template "${autoCreatedTemplateName}" was also created.`
        : 'Campaign created successfully!');
      handleClose();
      loadCampaigns();
      onSuccess();
    }
  };


  const handleUpdateCampaign = async () => {
    // Auto-create templates for new follow-ups without a template_id
    let processedTemplates = [...campaignData.templates];
    let autoCreatedTemplateName = null;
    const originalCount = originalTemplates.length;

    for (let i = originalCount; i < processedTemplates.length; i++) {
      if (!processedTemplates[i].template_id) {
        if (!processedTemplates[i].subject?.trim() || !processedTemplates[i].body?.trim()) {
          alert('Please select a template for the new follow-up, or provide a subject and body to create one automatically.');
          return;
        }
        const entry = processedTemplates[i];
        const autoName = entry.name?.trim() || `${campaignData.name} Follow-up ${i}`;
        const result = await createTemplate({ name: autoName, subject: entry.subject, body: entry.body, source: 'campaign_generated' });
        autoCreatedTemplateName = autoName;
        processedTemplates[i] = { ...entry, template_id: result.templateId };
      }
    }

    // Filter out templates with empty template_id
    const validTemplates = processedTemplates.filter(t => t.template_id);

    // Check if templates have changed compared to original
    let templatesChanged = haveTemplatesChanged(validTemplates, originalTemplates);

    const launchDateChanged = (campaignData.scheduled_at || '') !== originalScheduledAt;
    const timeZoneChanged = campaignData.time_zone !== originalTimeZone;
    const startTimeChanged = campaignData.start_time !== originalStartTime;

    templatesChanged = templatesChanged || timeZoneChanged || startTimeChanged;
    // Construct target_type with tags and click_count appended
    let targetType = campaignData.target_type;
    const tagsString = campaignData.selected_tags && campaignData.selected_tags.length > 0
      ? campaignData.selected_tags.join(',')
      : '';
    const clickCount = campaignData.click_count || '0';
    const companyIdsString = campaignData.selected_companies && campaignData.selected_companies.length > 0
      ? campaignData.selected_companies.join(',')
      : '';

    if (companyIdsString) {
      targetType = `${campaignData.target_type}|${tagsString}|${clickCount}|${companyIdsString}`;
    } else if (tagsString && clickCount !== '0') {
      targetType = `${campaignData.target_type}|${tagsString}|${clickCount}`;
    } else if (tagsString) {
      targetType = `${campaignData.target_type}|${tagsString}`;
    } else if (clickCount !== '0') {
      targetType = `${campaignData.target_type}||${clickCount}`;
    }

    const updateData = {
      // Campaign fields
      name: campaignData.name,
      target_type: targetType,
      email_account: campaignData.email_account,
      ...(launchDateChanged && { scheduled_at: campaignData.scheduled_at || null }),
      ...(timeZoneChanged && { time_zone: campaignData.time_zone || 'America/New_York' }),
      // Only include templates if they have changed
      ...(templatesChanged && { templates: validTemplates.length > 0 ? validTemplates : undefined }),
      ...(validTemplates.length > originalTemplates.length && {
        follow_up_eligibility: {
          include_out_of_office: includeOutOfOffice,
          include_replied: !excludeReplied,
          clicks_filter: validTemplates[originalTemplates.length]?.clicks_filter || 0
        }
      }),
      // Scheduling fields
      days: campaignData.days,
      ...(startTimeChanged && { start_time: campaignData.start_time || '06:00' }),
      end_time: campaignData.end_time,
      daily_limit: campaignData.daily_limit,
    };

    if (updateData.scheduled_at) {
      updateData.scheduled_at = utils.dateToUTC(updateData.scheduled_at, updateData.time_zone, updateData.start_time);
    }

    if (updateData.templates) {
      updateData.templates = updateData.templates.map(({ name: _n, subject: _s, body: _b, linkPreviewToggle: _l, ...t }) => ({
        ...t,
        scheduled_at: t.scheduled_at ? utils.dateToUTC(t.scheduled_at, campaignData.time_zone || 'America/New_York', campaignData.start_time) : t.scheduled_at
      }));
    }

    const response = await axios.put(`/api/email-campaigns/${editingCampaign.id}`, updateData);

    if (response.data.success) {
      alert(autoCreatedTemplateName
        ? `Campaign updated successfully!\nEmail template "${autoCreatedTemplateName}" was also created.`
        : 'Campaign updated successfully!');
      handleClose();
      loadCampaigns();
      onSuccess();
    }
  };

  const handleClose = () => {
    if (hasActiveBodyEdits && !window.confirm('Discard the unsaved email body changes?')) {
      return;
    }

    // Reset template form
    resetTemplateForm();
    // Reset campaign data
    setCampaignData(initialFormData);
    setTagPreview({ tag: null, contacts: [], loading: false, error: null });
    setCompanySearch('');
    setAudienceCount(null);
    setAudienceCountError(false);
    setFollowUpPreview(null);
    setIncludeOutOfOffice(false);
    setExcludeReplied(true);
    setActiveBodyEdits({});
    setIsInitialized(false);
    onClose();
  };

  if (!isOpen) return null;

  // Determine which contacts to show (selectedContacts for new campaigns, or campaignData.selected_contacts for edit mode)
  const contactsToShow = managedRecipients !== null
    ? managedRecipients
    : selectedContacts || campaignData.selected_contacts;
  const campaignRecipients = Array.isArray(contactsToShow) ? contactsToShow : [];
  const hasCampaignStarted = isEditMode && (
    campaignWasStarted
    || campaignRecipients.some(recipient => Boolean(recipient.last_sent_at))
  );
  const newFollowUpCount = isEditMode
    ? Math.max(0, (campaignData.templates?.length || 0) - originalTemplates.length)
    : 0;
  const firstSentAt = campaignRecipients
    .map(recipient => recipient.last_sent_at)
    .filter(Boolean)
    .sort()[0] || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
      <div className="h-full overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-6">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">{modalTitle}</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Simple Step Indicator - Only show for existing campaigns */}
            {isEditMode && editingCampaign && (
              <div className="mb-4 text-sm text-gray-600">
                Current Step: {Math.min(campaignData.templates?.length || 1, editingCampaign.current_round + 1 || 1)} of {campaignData.templates?.length || 1}
              </div>
            )}

            <form id="campaign-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Campaign Fields */}
              <div className="space-y-4">
                {/* Campaign Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={campaignData.name || ''}
                    onChange={handleCampaignInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., Q1 Cold Lead Outreach"
                    required
                    autoComplete="off"
                    disabled={loading}
                  />
                </div>

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Launch
                  </label>
                  {hasCampaignStarted ? (
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Initial email started {firstSentAt ? new Date(firstSentAt).toLocaleDateString() : campaignData.scheduled_at}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        The initial email start date is read-only because sending has begun.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="date"
                        name="scheduled_at"
                        value={campaignData.scheduled_at || ''}
                        onChange={handleCampaignInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                        disabled={loading}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        The initial email will start on this date. Leave it empty to save as a draft.
                      </p>
                    </div>
                  )}
                </div>

                {/* Tag Selection */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Filter by Lists (Optional)
                      {availableTags.length === 0 && !loadingTags && (
                        <span className="text-gray-500 text-xs"> - No tags available</span>
                      )}
                    </label>
                    {!contactsToShow && availableTags.length > 0 && (
                      <input
                        type="text"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        placeholder="Search tags..."
                        className="w-full sm:w-60 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loading || loadingTags}
                      />
                    )}
                  </div>
                  {contactsToShow ? (
                    <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
                      {campaignData.selected_tags && campaignData.selected_tags.length > 0 ? (
                        <div className="text-sm text-gray-600">
                          Selected tags: {campaignData.selected_tags.map(tag => (
                            <span key={tag} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-1">
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">No tags selected</div>
                      )}
                    </div>
                  ) : (
                    <div className="w-full p-3 border border-gray-300 rounded-lg min-h-[120px] max-h-[200px] overflow-y-auto">
                      {availableTags.length > 0 ? (
                        <div className="space-y-2">
                          {filteredTags.length > 0 ? (
                            filteredTags.map(tag => {
                              const isSelected = campaignData.selected_tags && campaignData.selected_tags.includes(tag);
                              return (
                                <label
                                  key={tag}
                                  className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200'
                                    }`}
                                  onClick={() => handleTagPreview(tag)}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      const currentTags = campaignData.selected_tags || [];
                                      const updatedTags = e.target.checked
                                        ? [...currentTags, tag]
                                        : currentTags.filter(t => t !== tag);
                                      handleCampaignInputChange({
                                        target: { name: 'selected_tags', value: updatedTags }
                                      });
                                    }}
                                    disabled={loading || loadingTags || isEditMode}
                                    className="rounded text-blue-600 focus:ring-blue-500 border-gray-300 mr-3"
                                  />
                                  <span className={`text-sm ${isSelected ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                                    {tag}
                                  </span>
                                </label>
                              );
                            })
                          ) : (
                            <p className="text-sm text-gray-500">No tags match your search.</p>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500 text-center py-4">No tags available</div>
                      )}
                    </div>
                  )}
                  {loadingTags && (
                    <p className="text-xs text-gray-500 mt-1">Loading tags...</p>
                  )}
                  {!contactsToShow && !isEditMode && (
                    <p className="text-xs text-gray-500 mt-1">
                      Check multiple tags to filter which contacts receive this campaign. Selected tags are highlighted in blue.
                      {campaignData.selected_tags && campaignData.selected_tags.length > 0 && (
                        <span className="block mt-1 font-medium text-blue-600">
                          Selected: {campaignData.selected_tags.join(', ')}
                        </span>
                      )}
                    </p>
                  )}
                  {tagPreview.tag && (
                    <div className="mt-3 border border-blue-200 rounded-lg p-3 bg-blue-50">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-blue-900">
                          Contacts tagged "{tagPreview.tag}"
                        </p>
                        <button
                          type="button"
                          onClick={() => setTagPreview({ tag: null, contacts: [], loading: false, error: null })}
                          className="text-xs text-blue-700 hover:underline"
                        >
                          Clear
                        </button>
                      </div>
                      {tagPreview.loading && (
                        <p className="text-xs text-blue-800">Loading contacts...</p>
                      )}
                      {!tagPreview.loading && tagPreview.error && (
                        <p className="text-xs text-red-600">{tagPreview.error}</p>
                      )}
                      {!tagPreview.loading && !tagPreview.error && (
                        <>
                          {tagPreview.contacts.length > 0 ? (
                            <ul className="max-h-32 overflow-y-auto space-y-1 text-sm text-gray-700">
                              {tagPreview.contacts.slice(0, 10).map(contact => (
                                <li key={`${contact.contact_status}-${contact.id}`}>
                                  {contact.first_name || contact.last_name
                                    ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
                                    : contact.email || 'Unnamed Contact'}
                                  {contact.email && (
                                    <span className="text-gray-500"> · {contact.email}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-gray-600">No contacts found for this tag.</p>
                          )}
                          {tagPreview.contacts.length > 10 && (
                            <p className="text-xs text-gray-500 mt-1">
                              + {tagPreview.contacts.length - 10} more not shown
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Company Selection */}
                {isEditMode ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Original Company Filter
                    </label>
                    <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
                      {campaignData.selected_companies?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {campaignData.selected_companies.map((companyId) => {
                            const company = availableCompanies.find(item => Number(item.id) === Number(companyId));
                            return (
                              <span
                                key={companyId}
                                className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                              >
                                {company?.name || `Company unavailable (ID ${companyId})`}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No company filter was used.</p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Original audience filters are read-only. Use Manage Contacts to change campaign recipients.
                    </p>
                  </div>
                ) : !contactsToShow && (
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Filter by Companies (Optional)
                      </label>
                      <input
                        type="text"
                        value={companySearch}
                        onChange={(event) => setCompanySearch(event.target.value)}
                        placeholder="Search companies..."
                        className="w-full sm:w-60 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loading || isEditMode}
                      />
                    </div>

                    {campaignData.selected_companies?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {campaignData.selected_companies.map((companyId) => {
                          const company = availableCompanies.find(item => Number(item.id) === Number(companyId));
                          return (
                            <span
                              key={companyId}
                              className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                            >
                              {company?.name || `Company ${companyId}`}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <div className="w-full p-3 border border-gray-300 rounded-lg min-h-[100px] max-h-[180px] overflow-y-auto">
                      {loadingCompanies && availableCompanies.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-3">Loading companies...</p>
                      ) : filteredCompanies.length > 0 ? (
                        <div className="space-y-2">
                          {filteredCompanies.map((company) => {
                            const companyId = Number(company.id);
                            const isSelected = (campaignData.selected_companies || []).includes(companyId);
                            return (
                              <label
                                key={company.id}
                                className={`flex items-center p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-white border border-gray-200'
                                  }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(event) => {
                                    const currentCompanies = campaignData.selected_companies || [];
                                    const updatedCompanies = event.target.checked
                                      ? [...currentCompanies, companyId]
                                      : currentCompanies.filter(id => id !== companyId);
                                    handleCampaignInputChange({
                                      target: { name: 'selected_companies', value: updatedCompanies }
                                    });
                                  }}
                                  disabled={loading || loadingCompanies || isEditMode}
                                  className="rounded text-blue-600 focus:ring-blue-500 border-gray-300 mr-3"
                                />
                                <span className={`text-sm ${isSelected ? 'text-blue-800 font-medium' : 'text-gray-700'}`}>
                                  {company.name}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-3">
                          {companySearch.trim() ? 'No companies match your search.' : 'No companies available.'}
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      With tags selected, contacts must match both a selected tag and a selected company. Without tags, companies apply across all contacts.
                    </p>
                  </div>
                )}

                {/* Target Audience */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {contactsToShow ? `Campaign Contacts (${contactsToShow.length})` : 'Tag by Engagement Status'}
                    </label>
                    {setShowContactPanel && editingCampaign && (
                      <button
                        type="button"
                        onClick={() => setShowContactPanel(true)}
                        className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      >
                        <Plus size={16} />
                        Manage Contacts
                      </button>
                    )}
                  </div>
                  {contactsToShow ? (
                    <div className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50">
                      {contactsToShow.slice(0, 5).map((contact, index) => (
                        <div key={index} className="text-sm text-gray-600">
                          {contact.first_name} {contact.last_name}
                          {contact.recipient_status === 'paused'
                            ? ' (future emails stopped)'
                            : contact.contact_status ? ` (${contact.contact_status})` : ''}
                        </div>
                      ))}
                      {contactsToShow.length > 5 && (
                        <div className="text-sm text-gray-500">
                          +{contactsToShow.length - 5} more
                        </div>
                      )}
                    </div>
                  ) : (
                    <select
                      name="target_type"
                      value={campaignData.target_type || 'cold'}
                      onChange={handleCampaignInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      disabled={loading || isEditMode}
                    >
                      <option value="cold">Cold Leads</option>
                      <option value="warm">Warm Prospects</option>
                      <option value="active">Active Clients</option>
                      <option value="mixed">Mixed Audience</option>
                    </select>
                  )}
                </div>

                {/* Click Filter */}
                {!contactsToShow && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Filter by Clicks (Optional)
                    </label>
                    <select
                      name="click_count"
                      value={campaignData.click_count || '0'}
                      onChange={handleCampaignInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      disabled={loading || isEditMode}
                    >
                      <option value="0">0 Clicks</option>
                      <option value="1">1 Click</option>
                      <option value="2">2 Clicks</option>
                      <option value="3">3 Clicks</option>
                      <option value="4">4 Clicks</option>
                      <option value="5">5 Clicks</option>
                      <option value="6">6 Clicks</option>
                      <option value="7">7 Clicks</option>
                      <option value="8">8 Clicks</option>
                      <option value="9">9 Clicks</option>
                      <option value="10">10 or more Clicks</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {campaignData.click_count === '0'
                        ? 'Showing: All contacts (0 or more clicks)'
                        : campaignData.click_count === '10'
                          ? 'Showing: Contacts with 10 or more clicks'
                          : `Showing: Contacts with ${campaignData.click_count} or more clicks`
                      }
                    </p>
                  </div>
                )}

                {!contactsToShow && !isEditMode && (
                  <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Matching contacts</p>
                      <p className="text-sm text-blue-800">
                        {loadingAudienceCount
                          ? 'Calculating...'
                          : audienceCountError
                            ? 'Count unavailable'
                            : `${audienceCount ?? 0} contact${audienceCount === 1 ? '' : 's'}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCampaignData(previous => ({
                          ...previous,
                          selected_tags: [],
                          selected_companies: [],
                          click_count: '0'
                        }));
                        setTagSearch('');
                        setCompanySearch('');
                        setTagPreview({ tag: null, contacts: [], loading: false, error: null });
                      }}
                      className="px-3 py-2 text-sm text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading || (
                        (!campaignData.selected_tags || campaignData.selected_tags.length === 0)
                        && (!campaignData.selected_companies || campaignData.selected_companies.length === 0)
                        && (campaignData.click_count || '0') === '0'
                      )}
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {/* Email Account Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Account {gmailAccounts.length === 0 && !loadingGmailAccounts && (
                      <span className="text-red-500 text-xs">(No Gmail accounts connected)</span>
                    )}
                  </label>
                  <select
                    name="email_account"
                    value={campaignData.email_account || ''}
                    onChange={handleCampaignInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    disabled={loading || loadingGmailAccounts}
                    required
                  >
                    <option value="">Select email account to send from</option>
                    {gmailAccounts.map(account => (
                      <option key={account.id} value={account.email}>
                        {account.account_name ? `${account.account_name} (${account.email})` : account.email}
                        {account.is_primary ? ' [Primary]' : ''}
                      </option>
                    ))}
                  </select>
                  {loadingGmailAccounts && (
                    <p className="text-xs text-gray-500 mt-1">Loading Gmail accounts...</p>
                  )}
                  {gmailAccounts.length === 0 && !loadingGmailAccounts && (
                    <p className="text-xs text-red-500 mt-1">
                      Please connect a Gmail account in Settings before creating a campaign.
                    </p>
                  )}
                </div>





                {/* Email Templates Management */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-700">Email Templates</h3>

                  {campaignData.templates?.map((template, index) => {
                    const isNewStep = isEditMode && index >= originalTemplates.length;
                    const sentToCount = campaignRecipients.filter(recipient => Number(recipient.round_number) > index).length;
                    const hasStepSent = isEditMode && (
                      (index === 0 && hasCampaignStarted)
                      || sentToCount > 0
                      || Number(editingCampaign?.current_round || 0) > index
                    );
                    const isDisabled = hasStepSent;
                    const stepStatus = isNewStep
                      ? 'New follow-up'
                      : sentToCount > 0 && sentToCount < campaignRecipients.length
                        ? 'Sending'
                        : hasStepSent
                          ? 'Sent'
                          : editingCampaign?.status === 'paused'
                            ? 'Paused'
                            : 'Scheduled';
                    const statusClass = stepStatus === 'Sent'
                      ? 'bg-green-100 text-green-700'
                      : stepStatus === 'Sending'
                        ? 'bg-blue-100 text-blue-700'
                        : stepStatus === 'Paused'
                          ? 'bg-amber-100 text-amber-800'
                          : stepStatus === 'New follow-up'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700';

                    return (
                      <div key={index} className={`border rounded-lg p-4 space-y-3 ${isDisabled ? 'border-gray-300 bg-gray-50' : 'border-gray-200'
                        }`}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium text-gray-700">
                              {index === 0 ? 'Initial Email' : `Follow-up ${index}`}
                            </h4>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass}`}>
                              {stepStatus}
                            </span>
                            {hasStepSent && <span className="text-xs text-gray-500">Read-only</span>}
                          </div>
                          {index > 0 && !hasStepSent && (
                            <button
                              type="button"
                              onClick={() => removeTemplate(index)}
                              className="text-red-600 hover:text-red-800 text-sm"
                              disabled={loading}
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          {/* Template Selection */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email Template {isDisabled && <span className="text-xs text-gray-500">(locked)</span>}
                            </label>
                            <SearchableTemplateSelect
                              templates={localTemplates}
                              value={template.template_id || ''}
                              onChange={(templateId) => handleTemplateChange(index, 'template_id', templateId)}
                              disabled={loading || isDisabled}
                            />
                          </div>

                          {/* Schedule Type for follow-up templates */}
                          {index > 0 && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Schedule Type {isDisabled && <span className="text-xs text-gray-500">(locked)</span>}
                              </label>

                              {/* Radio choice between frequency and date */}
                              <div className="flex space-x-4 mb-3">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`schedule_type_${index}`}
                                    checked={!template.use_date}
                                    onChange={() => handleTemplateChange(index, 'use_date', false)}
                                    disabled={loading || isDisabled}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">After previous email</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`schedule_type_${index}`}
                                    checked={template.use_date}
                                    onChange={() => handleTemplateChange(index, 'use_date', true)}
                                    disabled={loading || isDisabled}
                                    className="mr-2"
                                  />
                                  <span className="text-sm">Specific date</span>
                                </label>
                              </div>

                              {/* Frequency selector (shown when use_date is false) */}
                              {!template.use_date && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Send After Previous Email
                                  </label>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Amount</label>
                                      <input
                                        type="number"
                                        min="1"
                                        max="999"
                                        step="1"
                                        value={template.frequency_amount || 2}
                                        onChange={(event) => handleFrequencyChange(index, 'frequency_amount', event.target.value)}
                                        className={`w-full p-3 border rounded-lg ${isDisabled ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                                          }`}
                                        disabled={loading || isDisabled}
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs text-gray-500 mb-1">Unit</label>
                                      <select
                                        value={template.frequency_unit || 'weeks'}
                                        onChange={(event) => handleFrequencyChange(index, 'frequency_unit', event.target.value)}
                                        className={`w-full p-3 border rounded-lg ${isDisabled ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                                          }`}
                                        disabled={loading || isDisabled}
                                      >
                                        <option value="hours">Hours</option>
                                        <option value="days">Days</option>
                                        <option value="weeks">Weeks</option>
                                        <option value="months">Months</option>
                                      </select>
                                    </div>
                                  </div>
                                  {template.frequency_unit === 'months' && (
                                    <p className="text-xs text-gray-500 mt-1">Each month is calculated as 30 days.</p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    The delay is calculated separately from each contact's previous email.
                                  </p>
                                </div>
                              )}

                              {/* Date selector (shown when use_date is true) */}
                              {template.use_date && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-600 mb-1">
                                    Earliest Eligible Date
                                  </label>
                                  <input
                                    type="date"
                                    value={template.scheduled_at || ''}
                                    onChange={(e) => handleTemplateChange(index, 'scheduled_at', e.target.value)}
                                    className={`w-full p-3 border rounded-lg ${isDisabled ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                                      }`}
                                    disabled={loading || isDisabled}
                                  />
                                  <p className="text-xs text-gray-500 mt-1">
                                    Daily limits and delivery hours can spread sending across later dates.
                                  </p>
                                </div>
                              )}

                              {/* Clicks Filter for follow-up templates */}
                              <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-600 mb-1">
                                  Filter by Clicks (Optional)
                                </label>
                                <select
                                  value={template.clicks_filter || 0}
                                  onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    handleTemplateChange(index, 'clicks_filter', value);
                                  }}
                                  className={`w-48 p-3 border rounded-lg ${isDisabled ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                                    }`}
                                  disabled={loading || isDisabled}
                                >
                                  <option value={0}>0 click</option>
                                  <option value={1}>1+ clicks</option>
                                  <option value={2}>2+ clicks</option>
                                  <option value={3}>3+ clicks</option>
                                  <option value={4}>4+ clicks</option>
                                  <option value={5}>5+ clicks</option>
                                  <option value={6}>6+ clicks</option>
                                  <option value={7}>7+ clicks</option>
                                  <option value={8}>8+ clicks</option>
                                  <option value={9}>9+ clicks</option>
                                  <option value={10}>10+ clicks</option>
                                </select>
                              </div>
                            </div>
                          )}

                          {/* Template Preview - Show content when template is selected */}
                          {template.template_id && (
                            <div className="mt-3 border-t pt-3">
                              <h5 className="text-sm font-medium text-gray-700 mb-2">
                                Template Preview {index === 0 ? '(Initial Email)' : `(Follow-up ${index})`}
                              </h5>
                              <TemplatePreview
                                key={`${index}:${template.template_id}`}
                                template={getTemplateForDisplay(template.template_id) || editingCampaign?.templates?.find(t => t.template_id === template.template_id && t.sequence_order === index)}
                                campaignId={editingCampaign?.id}
                                campaignStatus={editingCampaign?.status}
                                canEditBody={isEditMode
                                  && !isNewStep
                                  && String(originalTemplates[index]?.template_id) === String(template.template_id)}
                                onTemplateUpdated={updateLocalTemplateBody}
                                editKey={`${index}:${template.template_id}`}
                                onBodyEditStateChange={handleBodyEditStateChange}
                              />
                            </div>
                          )}

                          {/* Show editable template fields for any template when no template is selected and not locked */}
                          {!template.template_id && !isDisabled && (
                            <div className="mt-3 border-t pt-3">
                              <h5 className="text-sm font-medium text-gray-700 mb-2">
                                {index === 0 ? 'Email Content (Initial Email)' : `Email Content (Follow-up ${index})`}
                              </h5>
                              {index === 0 ? (
                                <EmailTemplateFormFields
                                  formData={{ ...templateData }}
                                  onInputChange={handleInputChange}
                                  onFileSelect={handleFileSelect}
                                  onRemoveAttachment={handleRemoveAttachment}
                                  linkPreviews={linkPreviews}
                                  loadingPreview={loadingPreview}
                                  attachments={attachments}
                                  uploadingFile={uploadingFile}
                                  disabled={loading}
                                  compact={true}
                                  templateReadOnly={false}
                                />
                              ) : (
                                <EmailTemplateFormFields
                                  formData={{ name: template.name || '', type: 'Cold Leads', subject: template.subject || '', body: template.body || '', linkPreviewToggle: template.linkPreviewToggle || false }}
                                  onInputChange={(e) => {
                                    handleTemplateChange(index, e.target.name, e.target.value);
                                    handleInputChange(e);
                                  }}
                                  onFileSelect={handleFileSelect}
                                  onRemoveAttachment={handleRemoveAttachment}
                                  linkPreviews={linkPreviews}
                                  loadingPreview={loadingPreview}
                                  attachments={attachments}
                                  uploadingFile={uploadingFile}
                                  disabled={loading}
                                  compact={true}
                                  templateReadOnly={false}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )

                  })
                  }
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={addTemplate}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      disabled={loading}
                    >
                      + Add Follow-up Template
                    </button>
                  </div>

                  {newFollowUpCount > 0 && hasCampaignStarted && (
                    <div className="border border-purple-200 bg-purple-50 rounded-lg p-4 space-y-3">
                      <div>
                        <h4 className="font-medium text-purple-900">New Follow-up Recipient Preview</h4>
                        <p className="text-xs text-purple-700 mt-1">
                          Bounced, failed, paused, and opted-out contacts will not receive the new follow-up.
                        </p>
                      </div>

                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={excludeReplied}
                          onChange={(event) => setExcludeReplied(event.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          disabled={loading}
                        />
                        Do not send to contacts who replied
                      </label>

                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={includeOutOfOffice}
                          onChange={(event) => setIncludeOutOfOffice(event.target.checked)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          disabled={loading}
                        />
                        Include contacts with out-of-office replies
                      </label>

                      {loadingFollowUpPreview ? (
                        <p className="text-sm text-purple-700">Calculating recipient impact...</p>
                      ) : followUpPreview ? (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <div className="text-lg font-semibold text-gray-900">{followUpPreview.total}</div>
                              <div className="text-xs text-gray-500">Campaign contacts</div>
                            </div>
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <div className="text-lg font-semibold text-green-700">{followUpPreview.eligible_immediately}</div>
                              <div className="text-xs text-gray-500">Eligible next window</div>
                            </div>
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <div className="text-lg font-semibold text-blue-700">{followUpPreview.scheduled_later}</div>
                              <div className="text-xs text-gray-500">Scheduled later</div>
                            </div>
                            <div className="bg-white border rounded-lg p-3 text-center">
                              <div className="text-lg font-semibold text-red-700">{followUpPreview.excluded}</div>
                              <div className="text-xs text-gray-500">Excluded</div>
                            </div>
                          </div>
                          {followUpPreview.excluded > 0 && (
                            <div className="text-xs text-gray-600 flex flex-wrap gap-x-3 gap-y-1">
                              {Object.entries(followUpPreview.excluded_reasons || {})
                                .filter(([, count]) => count > 0)
                                .map(([reason, count]) => (
                                  <span key={reason}>{count} {reason.replaceAll('_', ' ')}</span>
                                ))}
                            </div>
                          )}
                          {followUpPreview.eligible_immediately > 0 && (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                              Contacts whose delay has already elapsed will send during the next allowed delivery window.
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-red-600">Recipient preview is unavailable. Try again before saving.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Scheduling Configuration */}
                <div className="space-y-4 border-t pt-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700">Delivery Settings</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      These settings apply to all future emails in this campaign.
                    </p>
                  </div>

                  {/* Daily Limits */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Daily Limits
                    </label>
                    <input
                      type="number"
                      name="daily_limit"
                      value={campaignData.daily_limit || 100}
                      onChange={handleCampaignInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      placeholder="100"
                      min="1"
                      max="1000"
                      disabled={loading}
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum number of emails to send per day</p>
                  </div>

                  {/* Select Days */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Days
                    </label>
                    <div className="grid grid-cols-7 gap-2">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => {
                        const fullDayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                        const selectedDays = campaignData.days ? JSON.parse(campaignData.days) : [];
                        const isSelected = selectedDays.includes(fullDayNames[index]);

                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => {
                              const currentDays = campaignData.days ? JSON.parse(campaignData.days) : [];
                              const fullDayName = fullDayNames[index];
                              let newDays;

                              if (currentDays.includes(fullDayName)) {
                                newDays = currentDays.filter(d => d !== fullDayName);
                              } else {
                                newDays = [...currentDays, fullDayName];
                              }

                              setCampaignData(prev => ({
                                ...prev,
                                days: JSON.stringify(newDays)
                              }));
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium ${isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            disabled={loading}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Configuration */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Time
                      </label>
                      <input
                        type="time"
                        name="start_time"
                        value={campaignData.start_time || '06:00'}
                        onChange={handleCampaignInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        disabled={loading}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Time
                      </label>
                      <input
                        type="time"
                        name="end_time"
                        value={campaignData.end_time || '18:00'}
                        onChange={handleCampaignInputChange}
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  {/* Timezone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timezone
                    </label>
                    <select
                      name="time_zone"
                      value={campaignData.time_zone || 'America/New_York'}
                      onChange={handleCampaignInputChange}
                      className="w-full p-3 border border-gray-300 rounded-lg"
                      disabled={loading}
                    >
                      <option value="America/New_York">America/New_York</option>
                      <option value="America/Los_Angeles">America/Los_Angeles</option>
                      <option value="America/Chicago">America/Chicago</option>
                      <option value="America/Denver">America/Denver</option>
                      <option value="Europe/London">Europe/London</option>
                      <option value="Europe/Paris">Europe/Paris</option>
                      <option value="Asia/Tokyo">Asia/Tokyo</option>
                      <option value="Australia/Sydney">Australia/Sydney</option>
                    </select>
                  </div>

                  {/* Active Summary */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="text-sm text-blue-700">
                      <strong>Active:</strong> {campaignData.days ? JSON.parse(campaignData.days).map(day => day.substring(0, 2)).join(', ') : 'Mo, Tu, We, Th, Fr'} • {campaignData.start_time || '06:00'} AM to {campaignData.end_time || '18:00'} PM ({campaignData.time_zone || 'America/New_York'}) • Max {campaignData.daily_limit || 100} emails/day
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  disabled={loading || hasActiveBodyEdits || (newFollowUpCount > 0 && hasCampaignStarted && loadingFollowUpPreview)}
                >
                  {loading
                    ? (isEditMode ? 'Updating...' : 'Creating...')
                    : newFollowUpCount > 0 && hasCampaignStarted
                      ? `Save and Queue Follow-up${newFollowUpCount > 1 ? 's' : ''}`
                      : isEditMode ? 'Update Campaign' : 'Create Campaign'}
                </button>
              </div>
              {hasActiveBodyEdits && (
                <p className="text-sm text-amber-700">
                  Save or cancel the email body edit before updating the campaign.
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;
